// src/components/marketing/WaitlistPage.tsx
import React, { useState, useEffect } from 'react';
import './WaitlistPage.css';

import FixedColorFireworks from './FixedColorFireworks';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

import sparklogo9 from '../layouts/images/sparklogo9.png';
import sparkbackground from '../layouts/images/sparkbackground.png';
import sparkmoon from '../layouts/images/sparkmoon2.png';

/* ------------------------------------------------------------------ */
/*  Background and moon artwork geometry                              */
/* ------------------------------------------------------------------ */
const BG_NATURAL_W = 2048;   // native width of sparkbackground.png (px)
const BG_NATURAL_H = 1055;   // native height of sparkbackground.png (px)
const MOON_NATURAL_W = 250;  // estimated width of sparkmoon.png (px)
const MOON_NATURAL_H = 250;  // estimated height of sparkmoon.png (px)
const MOON_CTR_RATIO = 0.207;  // 218 / 1055 – vertical center of the moon

const WaitlistPage: React.FC = () => {
  /* ───── local state ─────────────────────────────────────────────── */
  const [moonPosition, setMoonPosition] = useState<{top: number, left: number} | undefined>(undefined);
  const [moonSize, setMoonSize] = useState<{width: number, height: number} | undefined>(undefined);

  const [email, setEmail] = useState('');
  const [isSubmitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  /* ───── pixel-perfect moon alignment hook ───────────────────────── */
  useEffect(() => {
    const updateMoonPosition = () => {
      const wrapper = document.querySelector('.waitlist-wrapper') as HTMLElement;
      if (!wrapper) return;

      const { clientWidth: w, clientHeight: h } = wrapper;

      // scale factor chosen by CSS `background-size: cover`
      const scale = Math.max(w / BG_NATURAL_W, h / BG_NATURAL_H);

      // total BG height after scaling
      const drawnH = BG_NATURAL_H * scale;

      // vertical crop introduced by `cover`
      const cropY = (drawnH - h) / 2;

      // computed padding-top on the wrapper (e.g. 3vh → px)
      const paddingTop =
        parseFloat(getComputedStyle(wrapper).paddingTop) || 0;

      // final Y-coord of the moon's center inside the viewport
      const moonCenterY =
        BG_NATURAL_H * MOON_CTR_RATIO * scale - cropY + paddingTop;

      // Set the moon's position (centered horizontally)
      setMoonPosition({
        top: moonCenterY - (MOON_NATURAL_H * scale) / 2,
        left: w / 2 - (MOON_NATURAL_W * scale) / 2
      });

      // Set the moon's size based on the scale
      setMoonSize({
        width: MOON_NATURAL_W * scale,
        height: MOON_NATURAL_H * scale
      });
    };

    updateMoonPosition();  // run once at mount
    window.addEventListener('resize', updateMoonPosition);
    window.addEventListener('orientationchange', updateMoonPosition);
    return () => {
      window.removeEventListener('resize', updateMoonPosition);
      window.removeEventListener('orientationchange', updateMoonPosition);
    };
  }, []);

  /* ───── form submit handler ─────────────────────────────────────── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@') || !email.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await setDoc(
        doc(db, 'waitlist', `waitlist_${Date.now()}`),
        { email, timestamp: new Date() }
      );
      setSubmitted(true);
      setEmail('');
    } catch (err) {
      console.error('Error adding to waitlist:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ───── render ──────────────────────────────────────────────────── */
  return (
    /* NOTE: 2 class names so FixedColorFireworks can locate the wrapper */
    <div
      className="waitlist-wrapper synthwave-landing-container"
      style={{
        backgroundImage: `url(${sparkbackground})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <FixedColorFireworks hideBackground useBlueAndPurple />

      {/* Moon image positioned according to calculation */}
      {moonPosition && moonSize && (
        <div
          className="moon-image-container"
          style={{
            position: 'absolute',
            top: moonPosition.top,
            left: moonPosition.left,
            width: moonSize.width,
            height: moonSize.height,
            zIndex: 2
          }}
        >
          <img 
            src={sparkmoon} 
            alt="Moon" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </div>
      )}

      {/* logo locked to the moon's center */}
      <div
        className="moon-centered-logo"
        style={moonPosition ? { 
          position: 'absolute',
          top: moonPosition.top + (moonSize?.height || 0) / 2,
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 3,
          display: 'block',
          width: 'auto'
        } : undefined}
      >
        <img 
          src={sparklogo9} 
          alt="SPARK" 
          className="spark-logo" 
          style={{
            maxHeight: '150px',
            width: 'auto',
            display: 'block'
          }}
        />
      </div>

      <div className="content-wrapper">
        <div className="waitlist-container-new">
          <h1 className="waitlist-heading-new glow-text">Join the Waitlist</h1>
          <p className="waitlist-subheading-new glow-text">
            The party starts with the invite – give it a Spark
          </p>

          <p className="waitlist-bonus-text">
            Waitlist members will receive a 100% off coupon on launch day - coming VERY soon
          </p>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="waitlist-form-new">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="waitlist-input-new"
                disabled={isSubmitting}
              />

              <button
                type="submit"
                className="synthwave-button synthwave-button--primary synthwave-button--full-width"
                disabled={isSubmitting}
              >
                <span className="button-text">
                  {isSubmitting ? 'SUBMITTING…' : 'JOIN WAITLIST'}
                </span>
              </button>

              {error && <p className="waitlist-error-new">{error}</p>}
            </form>
          ) : (
            <div className="waitlist-success-new">
              <p>We can't wait to be a part of your next party!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WaitlistPage;